import React, { Suspense, useRef, useMemo, useState, useEffect, useCallback } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Float, useGLTF, View } from '@react-three/drei'
import * as THREE from 'three'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

export type ScrollPhase = 'pulse' | 'tetris' | 'network' | 'ledger'

// ── WebGL detection (shared with OriginCore3D) ─────────────────
let webGLSupportCache: boolean | null = null

function canUseWebGL(): boolean {
  if (typeof window === 'undefined') return false
  if (webGLSupportCache !== null) return webGLSupportCache

  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl')
    if (!gl) {
      webGLSupportCache = false
      return false
    }
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
    if (debugInfo) {
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      if (/SwiftShader|Software|llvmpipe/i.test(renderer)) {
        webGLSupportCache = false
        return false
      }
    }
    webGLSupportCache = true
    return true
  } catch {
    webGLSupportCache = false
    return false
  }
}

// ── Reduced motion check ──────────────────────────────────────
function usePrefersReducedMotion(): boolean {
  return useReducedMotion()
}

// ── Seeded randomness ─────────────────────────────────────────
function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000
  return x - Math.floor(x)
}

useGLTF.preload('/assets/3d/cube.glb')
useGLTF.preload('/assets/3d/bar.glb')

// ── Phase color palettes ──────────────────────────────────────
const PHASE_COLORS: Record<ScrollPhase, { primary: string; emissive: string; intensity: number }> =
  {
    pulse: { primary: '#005ea1', emissive: '#005ea1', intensity: 4 },
    tetris: { primary: '#7c3aed', emissive: '#7c3aed', intensity: 3 },
    network: { primary: '#059669', emissive: '#10b981', intensity: 3.5 },
    ledger: { primary: '#d97706', emissive: '#f59e0b', intensity: 3 },
  }

// We will use a wrapper that adds the Float and lights so we don't repeat it!
function GeometryWrapper({ phase, children }: { phase: ScrollPhase; children: React.ReactNode }) {
  const reducedMotion = usePrefersReducedMotion()
  return (
    <>
      <Float
        speed={reducedMotion ? 0 : 2}
        rotationIntensity={0.5}
        floatIntensity={reducedMotion ? 0 : 1}
      >
        {children}
      </Float>
      <ambientLight intensity={1} />
      <directionalLight
        position={[10, 10, 5]}
        intensity={PHASE_COLORS[phase].intensity}
        color={PHASE_COLORS[phase].primary}
      />
    </>
  )
}

// ── Phase: Pulse (the Origin orb) ──────────────────────────────
export function PulseGeometry() {
  const groupRef = useRef<THREE.Group>(null)
  const innerRef = useRef<THREE.Mesh>(null)
  const outerRef = useRef<THREE.Mesh>(null)
  const swarmRef = useRef<THREE.Points>(null)

  const particlesPosition = useMemo(() => {
    const count = 200
    const positions = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      const r = 2.5 + seededRandom(i * 3) * 2
      const theta = 2 * Math.PI * seededRandom(i * 3 + 1)
      const phi = Math.acos(2 * seededRandom(i * 3 + 2) - 1)
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta)
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta)
      positions[i * 3 + 2] = r * Math.cos(phi)
    }
    return positions
  }, [])

  useFrame((state, delta) => {
    if (innerRef.current) {
      innerRef.current.rotation.y += delta * 0.1
      innerRef.current.rotation.x += delta * 0.15
    }
    if (outerRef.current) {
      outerRef.current.rotation.y -= delta * 0.05
      outerRef.current.rotation.z += delta * 0.08
    }
    if (swarmRef.current) {
      swarmRef.current.rotation.y -= delta * 0.05
      swarmRef.current.rotation.z += delta * 0.02
    }
    if (groupRef.current) {
      const targetX = (state.pointer.y * Math.PI) / 8
      const targetY = (state.pointer.x * Math.PI) / 8
      groupRef.current.rotation.x += (targetX - groupRef.current.rotation.x) * 0.05
      groupRef.current.rotation.y += (targetY - groupRef.current.rotation.y) * 0.05
    }
  })

  return (
    <GeometryWrapper phase="pulse">
      <group ref={groupRef}>
        <mesh ref={innerRef}>
          <sphereGeometry args={[1.5, 32, 32]} />
          <meshStandardMaterial
            color="#005ea1"
            emissive="#005ea1"
            emissiveIntensity={4}
            transparent
            opacity={0.35}
            wireframe
          />
        </mesh>
        <mesh ref={outerRef}>
          <icosahedronGeometry args={[2.2, 1]} />
          <meshStandardMaterial color="#ffffff" transparent opacity={0.7} wireframe />
        </mesh>
        <points ref={swarmRef}>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" args={[particlesPosition, 3]} />
          </bufferGeometry>
          <pointsMaterial size={0.035} color="#005ea1" transparent opacity={0.6} sizeAttenuation />
        </points>
      </group>
    </GeometryWrapper>
  )
}

// ── Phase: Tetris (cube.glb grid) ─────────────────────────────
export function TetrisGeometry() {
  const groupRef = useRef<THREE.Group>(null)
  const { scene } = useGLTF('/assets/3d/cube.glb')

  const positions = useMemo(() => {
    const grid: THREE.Vector3[] = []
    for (let x = -1.5; x <= 1.5; x += 1) {
      for (let y = -1; y <= 1; y += 1) {
        for (let z = -0.5; z <= 0.5; z += 1) {
          const idx = grid.length
          grid.push(
            new THREE.Vector3(
              x + seededRandom(idx * 7 + 1) * 0.2 - 0.1,
              y + seededRandom(idx * 7 + 2) * 0.2 - 0.1,
              z + seededRandom(idx * 7 + 3) * 0.2 - 0.1
            )
          )
        }
      }
    }
    return grid
  }, [])

  useFrame((_, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.03
    }
  })

  const cubeGeometry = scene.children[0] as THREE.Mesh
  const geo = cubeGeometry?.geometry

  return (
    <GeometryWrapper phase="tetris">
      <group ref={groupRef}>
        {positions.map((pos, i) => (
          <mesh key={`tetris-cube-${i}`} position={pos} scale={0.25}>
            {geo ? (
              <primitive object={geo.clone()} attach="geometry" />
            ) : (
              <boxGeometry args={[1, 1, 1]} />
            )}
            <meshStandardMaterial
              color="#7c3aed"
              emissive="#7c3aed"
              emissiveIntensity={2}
              transparent
              opacity={0.7}
              wireframe
            />
          </mesh>
        ))}
      </group>
    </GeometryWrapper>
  )
}

// ── Phase: Network (procedural graph nodes + dynamic lines) ───
export function NetworkGeometry() {
  const groupRef = useRef<THREE.Group>(null)
  const lineRef = useRef<THREE.LineSegments>(null)

  const nodePositions = useMemo(() => {
    const nodes: THREE.Vector3[] = []
    const count = 16
    for (let i = 0; i < count; i++) {
      const r = 1.5 + seededRandom(i * 5) * 2
      const theta = 2 * Math.PI * seededRandom(i * 5 + 1)
      const phi = Math.acos(2 * seededRandom(i * 5 + 2) - 1)
      nodes.push(
        new THREE.Vector3(
          r * Math.sin(phi) * Math.cos(theta),
          r * Math.sin(phi) * Math.sin(theta),
          r * Math.cos(phi)
        )
      )
    }
    return nodes
  }, [])

  const lineGeometry = useMemo(() => {
    const positions: number[] = []
    const threshold = 2.8
    for (let i = 0; i < nodePositions.length; i++) {
      for (let j = i + 1; j < nodePositions.length; j++) {
        if (nodePositions[i].distanceTo(nodePositions[j]) < threshold) {
          positions.push(
            nodePositions[i].x,
            nodePositions[i].y,
            nodePositions[i].z,
            nodePositions[j].x,
            nodePositions[j].y,
            nodePositions[j].z
          )
        }
      }
    }
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
    return geo
  }, [nodePositions])

  useFrame((_, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.04
      groupRef.current.rotation.x += delta * 0.01
    }
  })

  return (
    <GeometryWrapper phase="network">
      <group ref={groupRef}>
        {nodePositions.map((pos, i) => (
          <mesh key={`network-node-${i}`} position={pos} scale={0.2}>
            <icosahedronGeometry args={[0.5, 1]} />
            <meshStandardMaterial
              color="#059669"
              emissive="#10b981"
              emissiveIntensity={2.5}
              transparent
              opacity={0.8}
              wireframe
            />
          </mesh>
        ))}
        <lineSegments ref={lineRef} geometry={lineGeometry}>
          <lineBasicMaterial color="#10b981" transparent opacity={0.4} />
        </lineSegments>
      </group>
    </GeometryWrapper>
  )
}

// ── Phase: Ledger (bar.glb stacking) ──────────────────────────
export function LedgerGeometry() {
  const groupRef = useRef<THREE.Group>(null)
  const { scene } = useGLTF('/assets/3d/bar.glb')

  const barPositions = useMemo(() => {
    const bars: { pos: THREE.Vector3; height: number }[] = []
    const count = 12
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI / (count - 1)) * i - Math.PI / 2
      const r = 2.2
      const height = 0.5 + seededRandom(i * 11) * 2
      bars.push({
        pos: new THREE.Vector3(r * Math.cos(angle), -1 + height * 0.5, r * Math.sin(angle)),
        height,
      })
    }
    return bars
  }, [])

  useFrame((_, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.02
    }
  })

  let barGeo = null
  if (scene && scene.children && scene.children.length > 0) {
    barGeo = (scene.children[0] as THREE.Mesh)?.geometry
  }

  return (
    <GeometryWrapper phase="ledger">
      <group ref={groupRef}>
        {barPositions.map(({ pos, height }, i) => (
          <mesh key={`ledger-bar-${i}`} position={pos} scale={[0.2, height * 0.5, 0.2]}>
            {barGeo ? (
              <primitive object={barGeo.clone()} attach="geometry" />
            ) : (
              <boxGeometry args={[1, 1, 1]} />
            )}
            <meshStandardMaterial
              color="#d97706"
              emissive="#f59e0b"
              emissiveIntensity={2}
              transparent
              opacity={0.75}
              wireframe
            />
          </mesh>
        ))}
      </group>
    </GeometryWrapper>
  )
}

// ── Global Canvas ────────────────────────────────────────────
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 2500
const MOUNT_DELAY_MS = 100

type EventSourceInput = React.RefObject<HTMLElement | null> | HTMLElement | null | undefined

function resolveEventSource(eventSource: EventSourceInput): HTMLElement | undefined {
  if (!eventSource) return undefined
  if (typeof eventSource === 'object' && 'current' in eventSource) {
    return eventSource.current ?? undefined
  }
  return eventSource
}

function PhaseGeometry({ phase }: { phase: ScrollPhase }) {
  switch (phase) {
    case 'tetris':
      return <TetrisGeometry />
    case 'network':
      return <NetworkGeometry />
    case 'ledger':
      return <LedgerGeometry />
    case 'pulse':
    default:
      return <PulseGeometry />
  }
}

export function GlobalCanvas({ eventSource }: { eventSource: EventSourceInput }) {
  const [remountKey, setRemountKey] = useState(0)
  const [ready, setReady] = useState(false)
  const retries = useRef(0)
  const [failed, setFailed] = useState(() => !canUseWebGL())
  const resolvedEventSource = resolveEventSource(eventSource)

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), MOUNT_DELAY_MS)
    return () => clearTimeout(timer)
  }, [])

  const handleContextLost = useCallback((e: Event) => {
    e.preventDefault()
    retries.current += 1
    if (retries.current <= MAX_RETRIES) {
      const delay = RETRY_DELAY_MS * retries.current
      console.warn(
        `[OriginGlobalCanvas] WebGL context lost — retry ${retries.current}/${MAX_RETRIES} in ${delay}ms`
      )
      setTimeout(() => setRemountKey((k) => k + 1), delay)
    } else {
      console.warn('[OriginGlobalCanvas] WebGL context lost — max retries reached, disabling 3D')
      setFailed(true)
    }
  }, [])

  const handleContextRestored = useCallback(() => {
    console.info('[OriginGlobalCanvas] WebGL context restored')
    retries.current = 0
  }, [])

  if (failed || !ready) {
    return null
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, opacity: 1 }}
      aria-hidden="true"
    >
      <Canvas
        key={remountKey}
        eventSource={resolvedEventSource}
        camera={{ position: [0, 0, 5.5], fov: 50 }}
        dpr={[1, 1.5]}
        gl={{
          alpha: true,
          antialias: false,
          powerPreference: 'low-power',
          preserveDrawingBuffer: false,
          failIfMajorPerformanceCaveat: true,
        }}
        frameloop="always"
        onCreated={({ gl }) => {
          const canvas = gl.domElement
          canvas.addEventListener('webglcontextlost', handleContextLost)
          canvas.addEventListener('webglcontextrestored', handleContextRestored)
        }}
        className="w-full h-full"
      >
        <Suspense fallback={null}>
          <View.Port />
        </Suspense>
      </Canvas>
    </div>
  )
}

export const OriginGlobalCanvas = GlobalCanvas

export type OriginPulseSceneProps = {
  phase?: ScrollPhase
  className?: string
}

export default function OriginPulseScene({ phase = 'pulse', className }: OriginPulseSceneProps) {
  const [remountKey, setRemountKey] = useState(0)
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(() => !canUseWebGL())
  const retries = useRef(0)

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), MOUNT_DELAY_MS)
    return () => clearTimeout(timer)
  }, [])

  const handleContextLost = useCallback((e: Event) => {
    e.preventDefault()
    retries.current += 1
    if (retries.current <= MAX_RETRIES) {
      const delay = RETRY_DELAY_MS * retries.current
      setTimeout(() => setRemountKey((k) => k + 1), delay)
    } else {
      setFailed(true)
    }
  }, [])

  if (failed || !ready) {
    return null
  }

  return (
    <div
      data-testid="origin-pulse-scene"
      aria-hidden="true"
      className={`origin-pulse-geometry-container pointer-events-none fixed inset-0 z-0 ${className ?? ''}`}
    >
      <Canvas
        key={remountKey}
        camera={{ position: [0, 0, 5.5], fov: 50 }}
        dpr={[1, 1.5]}
        gl={{
          alpha: true,
          antialias: false,
          powerPreference: 'low-power',
          preserveDrawingBuffer: false,
          failIfMajorPerformanceCaveat: true,
        }}
        frameloop="always"
        onCreated={({ gl }) => {
          const canvas = gl.domElement
          canvas.addEventListener('webglcontextlost', handleContextLost)
        }}
        className="w-full h-full"
      >
        <Suspense fallback={null}>
          <PhaseGeometry phase={phase} />
        </Suspense>
      </Canvas>
    </div>
  )
}
